import $ from 'jquery';

import AlerterView from 'chairisher/view/alerter';

import { getAmplitudeExperimentFlagMap } from 'chairisher/context/experiments';
import { exposeExperiment } from 'chairisher/thirdparty/experiments';
import { logError } from 'chairisher/util/analytics';
import { submitForm } from 'chairisher/util/fetch';

/**
 * Binds the $cartForm such that the cart drawer opens when the form is submitted, if not previously open.
 * If the cart drawer is already present, the respective cart item is instead either added or updated.
 *
 * Note: keeping this in its own file allows clients to import this without bringing in all the checkout dependencies.
 * This benefits bundle size. These dependencies are lazy loaded with webpack when the button is clicked.
 *
 * @param {Object} options Options to pass to the bindAddToCart function
 * @param {jQuery} options.$cartForm The form for the "add to cart" cta
 * @param {Function=} options.onSuccess Optional function to call when item is successfully added to the cart
 */
export default function bindAddToCart({ $cartForm, onSuccess = () => {} }) {
    const $submitButton = $cartForm.find('button[type="submit"]');
    let isSubmitting = false;

    $cartForm.on('submit', async (e) => {
        e.preventDefault();

        // Prevent concurrent submissions using synchronous flag to eliminate race condition window
        if (isSubmitting || $submitButton.prop('disabled')) {
            return;
        }

        isSubmitting = true;
        $submitButton.prop('disabled', true);

        const quantity = parseInt($cartForm.find('[name="quantity"]').val(), 10) || 1;

        try {
            const { default: cartDrawer } = await import('chairisher/component/cart');
            const { data, html } = await submitForm($cartForm.get(0));

            if (!cartDrawer.isInitialized()) {
                cartDrawer.initialize(data, html).done(async () => {
                    cartDrawer.show();
                });
            } else {
                cartDrawer.setData(data);

                const cartItemSelector = `.js-cart-item[data-product-id="${data.added_to_cart_product_id}"]`;
                const $cartBody = cartDrawer.$el.find('.js-cart-body');

                // cart item may already exist if it has additional quantities available,
                // or if it was previously removed, but still able to be added back
                const $existingCartItem = $cartBody.find(cartItemSelector);
                // when should_return_cart_item is included, the html returned is the js-cart-item element
                const $addedCartItem = $(html);

                if ($existingCartItem.length > 0) {
                    $existingCartItem.slideToggle(() => {
                        $existingCartItem.replaceWith($addedCartItem[0]);
                        cartDrawer.refreshCart();
                    });
                } else {
                    const $rail = $cartBody.find('.js-cart-rail-container');
                    if ($rail.length > 0) {
                        $rail.before($addedCartItem);
                    } else {
                        $cartBody.append($addedCartItem);
                    }
                    cartDrawer.refreshCart();
                }
            }

            // @TODO (CHAIR-19220): Remove after test
            exposeExperiment(getAmplitudeExperimentFlagMap().MAO_EXPRESS_CHECKOUT_IMPROVEMENTS);

            if (onSuccess) {
                onSuccess(quantity);
            }
        } catch (error) {
            AlerterView.error();
            logError('cart form submit', error);
        } finally {
            isSubmitting = false;
            $submitButton.prop('disabled', false);
        }
    });
}
